import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

// material-ui
import { useTheme, alpha } from '@mui/material/styles';
import { Collapse, List, ListItemButton, ListItemIcon, ListItemText, Typography, Chip, Tooltip } from '@mui/material';

// project imports
import NavItem from '../NavItem';
import menuItem, { categoryStyles } from 'menu-items';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { IconChevronDown, IconChevronUp } from '@tabler/icons';

// ==============================|| SIDEBAR MENU LIST COLLAPSE ITEMS ||============================== //

const NavCollapse = ({ menu, level }) => {
  const theme = useTheme();
  const customization = useSelector((state) => state.customization);
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const handleClick = () => {
    setOpen(!open);
    setSelected(!selected ? menu.id : null);
  };

  const { pathname } = useLocation();
  const checkOpenForParent = (child, id) => {
    child.forEach((item) => {
      if (item.url === pathname) {
        setOpen(true);
        setSelected(id);
      }
    });
  };

  // menu collapse for sub-levels
  useEffect(() => {
    setOpen(false);
    setSelected(null);
    if (menu.children) {
      menu.children.forEach((item) => {
        if (item.children?.length) {
          checkOpenForParent(item.children, menu.id);
        }
        if (item.url === pathname) {
          setSelected(menu.id);
          setOpen(true);
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, menu.children]);

  // menu collapse & item
  const menus = menu.children?.map((item) => {
    switch (item.type) {
      case 'collapse':
        return <NavCollapse key={item.id} menu={item} level={level + 1} />;
      case 'item':
        return <NavItem key={item.id} item={item} level={level + 1} />;
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            Menu Items Error
          </Typography>
        );
    }
  });

  const Icon = menu.icon;
  const menuIcon = menu.icon ? (
    <Icon
      strokeWidth={1.5}
      size="1.3rem"
      style={{
        marginTop: 'auto',
        marginBottom: 'auto',
        color: selected === menu.id && categoryStyles ? categoryStyles[menu.category]?.color || theme.palette.primary.main : 'inherit'
      }}
    />
  ) : (
    <FiberManualRecordIcon
      sx={{
        width: selected === menu.id ? 8 : 6,
        height: selected === menu.id ? 8 : 6,
        color: selected === menu.id && categoryStyles ? categoryStyles[menu.category]?.color || theme.palette.primary.main : 'inherit'
      }}
      fontSize={level > 0 ? 'inherit' : 'medium'}
    />
  );

  const categoryColor =
    categoryStyles && menu.category ? categoryStyles[menu.category]?.color || theme.palette.primary.main : theme.palette.primary.main;
  const categoryBgColor =
    categoryStyles && menu.category
      ? categoryStyles[menu.category]?.bgColor || alpha(theme.palette.primary.main, 0.08)
      : alpha(theme.palette.primary.main, 0.08);
  const categoryHoverColor =
    categoryStyles && menu.category
      ? categoryStyles[menu.category]?.hoverColor || alpha(theme.palette.primary.main, 0.12)
      : alpha(theme.palette.primary.main, 0.12);

  return (
    <>
      <Tooltip title={menu.tooltip || ''} placement="right" arrow>
        <ListItemButton
          sx={{
            borderRadius: `${customization.borderRadius}px`,
            mb: 0.5,
            alignItems: 'flex-start',
            backgroundColor: level > 1 ? 'transparent !important' : 'inherit',
            py: level > 1 ? 1 : 1.25,
            pl: `${level * 24}px`,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: categoryHoverColor,
              transform: 'translateX(4px)'
            },
            '&.Mui-selected': {
              backgroundColor: categoryBgColor,
              '&:hover': {
                backgroundColor: categoryHoverColor
              }
            }
          }}
          selected={selected === menu.id}
          onClick={handleClick}
        >
          <ListItemIcon
            sx={{
              my: 'auto',
              minWidth: !menu.icon ? 18 : 36,
              color: selected === menu.id ? categoryColor : 'inherit'
            }}
          >
            {menuIcon}
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography
                variant={selected === menu.id ? 'h5' : 'body1'}
                color={selected === menu.id ? categoryColor : 'inherit'}
                sx={{
                  my: 'auto',
                  fontWeight: selected === menu.id ? 600 : 'inherit'
                }}
              >
                {menu.title}
              </Typography>
            }
            secondary={
              menu.caption && (
                <Typography variant="caption" sx={{ ...theme.typography.subMenuCaption }} display="block" gutterBottom>
                  {menu.caption}
                </Typography>
              )
            }
          />
          {menu.badge && <Chip label={menu.badge.text} color={menu.badge.color || 'primary'} size="small" sx={{ height: 20, mr: 1 }} />}
          {open ? (
            <IconChevronUp stroke={1.5} size="1rem" style={{ marginTop: 'auto', marginBottom: 'auto' }} />
          ) : (
            <IconChevronDown stroke={1.5} size="1rem" style={{ marginTop: 'auto', marginBottom: 'auto' }} />
          )}
        </ListItemButton>
      </Tooltip>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List
          component="div"
          disablePadding
          sx={{
            position: 'relative',
            '&:after': {
              content: "''",
              position: 'absolute',
              left: '32px',
              top: 0,
              height: '100%',
              width: '1px',
              opacity: 1,
              background: categoryColor
            }
          }}
        >
          {menus}
        </List>
      </Collapse>
    </>
  );
};

NavCollapse.propTypes = {
  menu: PropTypes.object,
  level: PropTypes.number
};

export default NavCollapse;
